/**
 * The reservation form's data rules, kept out of the component so the one thing
 * that must not drift — the shape of the request — is testable on its own.
 */

/**
 * Every property `CreateReservationDto` declares.
 *
 * `forbidNonWhitelisted: true` makes anything else a 400 on the whole
 * reservation, which is what the design's `Email` field would be: `Reservation`
 * has no email column and the route matches or creates a customer **by phone**.
 */
export const RESERVATION_DTO_FIELDS = [
  'branchId',
  'tableId',
  'resourceId',
  'customerId',
  'customerName',
  'customerPhone',
  'partySize',
  'startsAt',
  'endsAt',
  'notes',
] as const;

/** `reservations.service.ts` — `endsAt` defaults to start + 90 minutes. */
export const DEFAULT_SLOT_MINUTES = 90;

export interface ReservationFormState {
  branchId: string;
  tableId: string;
  resourceId: string;
  customerName: string;
  customerPhone: string;
  partySize: string;
  /** A local `datetime-local` value, `yyyy-mm-ddThh:mm`. */
  startsAt: string;
  endsAt: string;
  notes: string;
}

/** §20 — a slot holds exactly one table **or** one resource, never both. */
export type ReservationSubject = 'table' | 'resource';

function text(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * The body of `POST /reservations`, built from an allowlist.
 *
 * `assertExactlyOneSubject` refuses a payload carrying both ids, so the subject
 * the operator chose decides which one is sent and the other is left out
 * entirely rather than sent empty.
 */
export function reservationBody(
  form: ReservationFormState,
  subject: ReservationSubject,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    branchId: form.branchId.trim(),
    partySize: Number(form.partySize) || 1,
    startsAt: new Date(form.startsAt).toISOString(),
  };

  if (subject === 'table') {
    const tableId = text(form.tableId);
    if (tableId) body.tableId = tableId;
  } else {
    const resourceId = text(form.resourceId);
    if (resourceId) body.resourceId = resourceId;
  }

  const optional = {
    customerName: text(form.customerName),
    customerPhone: text(form.customerPhone),
    notes: text(form.notes),
  };
  for (const [key, value] of Object.entries(optional)) {
    if (value !== undefined) body[key] = value;
  }

  if (form.endsAt.trim()) body.endsAt = new Date(form.endsAt).toISOString();

  return body;
}

/** What the server will use when `endsAt` is left blank, for the summary line. */
export function defaultEndsAt(startsAt: string): string {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return '';
  start.setMinutes(start.getMinutes() + DEFAULT_SLOT_MINUTES);
  return localInput(start);
}

/** `yyyy-mm-ddThh:mm` in the viewer's own timezone — what the inputs speak. */
function localInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** Joins the day strip's choice and the time chip's choice into one value. */
export function slotIso(day: string, time: string): string {
  return `${day}T${time}`;
}

/** The `hh:mm` of a `yyyy-mm-ddThh:mm`, so a chip can show as selected. */
export function timeOf(startsAt: string): string {
  return startsAt.includes('T') ? startsAt.slice(startsAt.indexOf('T') + 1, startsAt.indexOf('T') + 6) : '';
}

/** The `yyyy-mm-dd` of a `yyyy-mm-ddThh:mm`. */
export function dayOf(startsAt: string): string {
  return startsAt.slice(0, 10);
}

/**
 * The service window as half-hour chips.
 *
 * Deliberately **not** availability: `GET /reservations/availability` answers
 * one subject and one window, so a grid of ten simultaneously enabled/disabled
 * chips would be ten calls per table. These are the times a host may pick; the
 * Check availability button is still what answers whether one is free.
 */
export function timeSlots(from = '18:00', to = '22:30', stepMinutes = 30): string[] {
  const minutes = (value: string) => {
    const [h, m] = value.split(':').map(Number);
    return h * 60 + m;
  };
  const label = (total: number) =>
    `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;

  const out: string[] = [];
  for (let at = minutes(from); at <= minutes(to); at += stepMinutes) out.push(label(at));
  return out;
}

/** The Sunday-first week containing `day`, as `yyyy-mm-dd` strings. */
export function weekStrip(day: string): string[] {
  const anchor = new Date(`${day}T12:00:00`);
  if (Number.isNaN(anchor.getTime())) return [];
  const sunday = new Date(anchor);
  sunday.setDate(sunday.getDate() - sunday.getDay());
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(sunday);
    date.setDate(sunday.getDate() + index);
    return localInput(date).slice(0, 10);
  });
}

/** Moves the strip a whole week, keeping the weekday the operator was on. */
export function shiftWeek(day: string, weeks: number): string {
  const date = new Date(`${day}T12:00:00`);
  date.setDate(date.getDate() + weeks * 7);
  return localInput(date).slice(0, 10);
}
